import { useState, useMemo } from 'react';
import { demoGuests, demoBookings, demoPayments, demoRooms, demoRoomBeds } from '@/mocks/demoData';
import type { Guest } from '@/mocks/data';
import { useApp } from '@/contexts/AppContext';

export default function BookingGuests() {
  const { addToast } = useApp();
  const [guests, setGuests] = useState<Guest[]>(demoGuests);
  const [search, setSearch] = useState('');
  const [filterRepeating, setFilterRepeating] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingGuest, setEditingGuest] = useState<Guest | null>(null);
  const [showDetail, setShowDetail] = useState<Guest | null>(null);
  const [formData, setFormData] = useState({
    full_name: '', phone: '', email: '', id_proof_type: 'Aadhaar', id_proof_number: '', address: ''
  });

  const filtered = useMemo(() => {
    return guests.filter((g) => {
      const q = search.toLowerCase();
      if (search && !g.full_name.toLowerCase().includes(q) && !g.phone.includes(q) && !g.id_proof_number.toLowerCase().includes(q)) return false;
      if (filterRepeating === 'yes' && !g.is_repeating) return false;
      if (filterRepeating === 'no' && g.is_repeating) return false;
      return true;
    });
  }, [guests, search, filterRepeating]);

  const openAdd = () => {
    setEditingGuest(null);
    setFormData({ full_name: '', phone: '', email: '', id_proof_type: 'Aadhaar', id_proof_number: '', address: '' });
    setShowForm(true);
  };

  const openEdit = (g: Guest) => {
    setEditingGuest(g);
    setFormData({ full_name: g.full_name, phone: g.phone, email: g.email, id_proof_type: g.id_proof_type, id_proof_number: g.id_proof_number, address: g.address });
    setShowForm(true);
  };

  const handleSave = () => {
    if (!formData.full_name || !formData.phone) { addToast('error', 'Name and phone are required'); return; }
    if (editingGuest) {
      setGuests((prev) => prev.map((g) => g.id === editingGuest.id ? { ...g, ...formData } : g));
      addToast('success', 'Guest updated');
    } else {
      setGuests((prev) => [...prev, { id: `guest-${Date.now()}`, ...formData, is_repeating: false, total_spend: 0, created_at: new Date().toISOString() }]);
      addToast('success', 'Guest added');
    }
    setShowForm(false);
  };

  const handleDelete = (id: string) => {
    if (demoBookings.some((b) => b.guest_id === id)) { addToast('error', 'Cannot delete guest with bookings'); return; }
    if (confirm('Delete this guest?')) { setGuests((prev) => prev.filter((g) => g.id !== id)); addToast('success', 'Guest deleted'); }
  };

  const getGuestHistory = (guestId: string) => {
    return demoBookings.filter((b) => b.guest_id === guestId).sort((a, b) => new Date(b.check_in).getTime() - new Date(a.check_in).getTime());
  };

  const getGuestPayments = (guestId: string) => {
    const bookingIds = demoBookings.filter((b) => b.guest_id === guestId).map((b) => b.id);
    return demoPayments.filter((p) => bookingIds.includes(p.booking_id));
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-navy-800">Guest Directory</h1>
          <p className="text-navy-500 mt-1">Guest profiles, stay history, and repeat guest tracking.</p>
        </div>
        <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2.5 bg-navy-700 text-white rounded-lg hover:bg-navy-800 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-add-line" /> Add Guest
        </button>
      </div>

      <div className="bg-white rounded-xl border border-navy-100 p-4 mb-6">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-navy-500 mb-1.5">Search</label>
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-navy-400" />
              <input type="text" placeholder="Name, phone, ID proof..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-9 pr-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-navy-500 mb-1.5">Guest Type</label>
            <select value={filterRepeating} onChange={(e) => setFilterRepeating(e.target.value)} className="px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500">
              <option value="all">All Guests</option>
              <option value="yes">Repeat Guests</option>
              <option value="no">New Guests</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((g) => {
          const history = getGuestHistory(g.id);
          return (
            <div key={g.id} className="bg-white rounded-xl border border-navy-100 overflow-hidden hover:shadow-md transition-shadow">
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-navy-700 text-white flex items-center justify-center text-sm font-bold">{g.full_name.split(' ').map((n) => n[0]).join('').slice(0, 2)}</div>
                    <div>
                      <div className="font-semibold text-navy-800">{g.full_name} {g.is_repeating && <span className="ml-1 text-xs bg-gold-100 text-gold-700 px-2 py-0.5 rounded-full">Repeat</span>}</div>
                      <div className="text-xs text-navy-500">{g.phone}</div>
                    </div>
                  </div>
                </div>
                <div className="space-y-1.5 text-sm mb-4">
                  <div className="text-navy-500"><i className="ri-mail-line mr-2 text-navy-400" />{g.email || '-'}</div>
                  <div className="text-navy-500"><i className="ri-id-card-line mr-2 text-navy-400" />{g.id_proof_type}: {g.id_proof_number}</div>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-navy-100">
                  <div>
                    <div className="text-xs text-navy-400">Total Stays</div>
                    <div className="font-semibold text-navy-800">{history.length}</div>
                  </div>
                  <div>
                    <div className="text-xs text-navy-400">Total Spend</div>
                    <div className="font-semibold text-navy-800">Rs. {g.total_spend.toLocaleString('en-IN')}</div>
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <button onClick={() => setShowDetail(g)} className="flex-1 px-3 py-2 text-xs font-medium text-navy-700 bg-navy-50 rounded-lg hover:bg-navy-100 transition-colors cursor-pointer"><i className="ri-eye-line mr-1" /> View</button>
                  <button onClick={() => openEdit(g)} className="px-3 py-2 text-xs font-medium text-navy-700 bg-navy-50 rounded-lg hover:bg-navy-100 transition-colors cursor-pointer"><i className="ri-edit-line" /></button>
                  <button onClick={() => handleDelete(g.id)} className="px-3 py-2 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors cursor-pointer"><i className="ri-delete-bin-line" /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 overflow-hidden max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-navy-100 flex items-center justify-between">
              <h3 className="font-semibold text-navy-800">{editingGuest ? 'Edit Guest' : 'Add Guest'}</h3>
              <button onClick={() => setShowForm(false)} className="text-navy-400 hover:text-navy-600 cursor-pointer"><i className="ri-close-line text-xl" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-navy-700 mb-1.5">Full Name *</label><input type="text" className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={formData.full_name} onChange={(e) => setFormData((p) => ({ ...p, full_name: e.target.value }))} /></div>
                <div><label className="block text-sm font-medium text-navy-700 mb-1.5">Phone *</label><input type="text" className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={formData.phone} onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))} /></div>
              </div>
              <div><label className="block text-sm font-medium text-navy-700 mb-1.5">Email</label><input type="email" className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={formData.email} onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))} /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-navy-700 mb-1.5">ID Proof Type</label><select className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={formData.id_proof_type} onChange={(e) => setFormData((p) => ({ ...p, id_proof_type: e.target.value }))}><option>Aadhaar</option><option>Passport</option><option>Driving License</option></select></div>
                <div><label className="block text-sm font-medium text-navy-700 mb-1.5">ID Proof Number</label><input type="text" className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={formData.id_proof_number} onChange={(e) => setFormData((p) => ({ ...p, id_proof_number: e.target.value }))} /></div>
              </div>
              <div><label className="block text-sm font-medium text-navy-700 mb-1.5">Address</label><textarea rows={2} className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500 resize-none" value={formData.address} onChange={(e) => setFormData((p) => ({ ...p, address: e.target.value }))} /></div>
            </div>
            <div className="px-6 py-4 border-t border-navy-100 flex gap-3">
              <button onClick={() => setShowForm(false)} className="flex-1 px-4 py-2.5 border border-navy-200 rounded-lg text-sm font-medium text-navy-600 hover:bg-navy-50 transition-colors cursor-pointer">Cancel</button>
              <button onClick={handleSave} className="flex-1 px-4 py-2.5 bg-navy-700 text-white rounded-lg text-sm font-medium hover:bg-navy-800 transition-colors cursor-pointer">Save Guest</button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 overflow-hidden max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-navy-100 flex items-center justify-between">
              <h3 className="font-semibold text-navy-800">Guest History</h3>
              <button onClick={() => setShowDetail(null)} className="text-navy-400 hover:text-navy-600 cursor-pointer"><i className="ri-close-line text-xl" /></button>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-4 mb-6 p-4 bg-navy-50 rounded-lg">
                <div className="w-14 h-14 rounded-full bg-navy-700 text-white flex items-center justify-center text-lg font-bold">{showDetail.full_name.split(' ').map((n) => n[0]).join('').slice(0, 2)}</div>
                <div>
                  <div className="font-semibold text-navy-800 text-lg">{showDetail.full_name} {showDetail.is_repeating && <span className="ml-2 text-xs bg-gold-100 text-gold-700 px-2 py-0.5 rounded-full">Repeat</span>}</div>
                  <div className="text-sm text-navy-500">{showDetail.phone} | Total Spend: Rs. {showDetail.total_spend.toLocaleString('en-IN')}</div>
                  <div className="flex items-center gap-3 mt-1.5 text-xs text-navy-400">
                    <span><i className="ri-hotel-line mr-1" />{getGuestHistory(showDetail.id).filter((b) => !b.is_bed_booking).length} Room stays</span>
                    <span className="text-teal-600"><i className="ri-hotel-bed-line mr-1" />{getGuestHistory(showDetail.id).filter((b) => b.is_bed_booking).length} Bed stays</span>
                  </div>
                </div>
              </div>
              <h4 className="font-medium text-navy-800 mb-3">Stay History ({getGuestHistory(showDetail.id).length})</h4>
              <div className="space-y-3 mb-6">
                {getGuestHistory(showDetail.id).map((h) => {
                  const r = demoRooms.find((room) => room.id === h.room_id);
                  const bedNums = h.is_bed_booking && h.bed_ids.length > 0
                    ? h.bed_ids.map((bid) => {
                        const bed = demoRoomBeds.find((x) => x.id === bid);
                        return bed ? `B${bed.bed_number}` : '';
                      }).filter(Boolean)
                    : [];
                  return (
                    <div key={h.id} className="p-3 border border-navy-100 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-navy-800 text-xs">{h.booking_id}</span>
                          {h.is_bed_booking && (
                            <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full font-medium">
                              <i className="ri-hotel-bed-line mr-0.5" />Bed Booking
                            </span>
                          )}
                          {!h.is_bed_booking && (
                            <span className="text-xs bg-navy-100 text-navy-600 px-2 py-0.5 rounded-full">
                              <i className="ri-hotel-line mr-0.5" />Room
                            </span>
                          )}
                        </div>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-navy-100 text-navy-600 capitalize">{h.status.replace('_', ' ')}</span>
                      </div>
                      <div className="text-xs text-navy-500">
                        {r?.room_number} ({r?.category})
                        {' | '}{new Date(h.check_in).toLocaleDateString('en-IN')} - {new Date(h.check_out).toLocaleDateString('en-IN')}
                      </div>
                      {h.is_bed_booking && bedNums.length > 0 && (
                        <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                          {bedNums.map((bn, idx) => (
                            <span key={idx} className="text-xs bg-teal-50 text-teal-700 border border-teal-200 px-2 py-0.5 rounded-md font-medium">
                              <i className="ri-hotel-bed-line mr-1" />{bn}
                            </span>
                          ))}
                          <span className="text-xs text-navy-400">{h.bed_count} bed{h.bed_count > 1 ? 's' : ''} · Rs. {(h.total_amount / h.bed_count).toLocaleString('en-IN')}/bed</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-navy-400 capitalize">{h.booking_type.replace('_', ' ')}</span>
                        <span className="text-sm font-semibold text-navy-800">
                          Rs. {(h.total_amount + h.extra_charges.reduce((s, e) => s + e.amount, 0)).toLocaleString('en-IN')}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <h4 className="font-medium text-navy-800 mb-3">Payments ({getGuestPayments(showDetail.id).length})</h4>
              <div className="space-y-2">
                {getGuestPayments(showDetail.id).map((p) => (
                  <div key={p.id} className="flex items-center justify-between p-3 bg-navy-50 rounded-lg text-sm">
                    <div className="text-navy-600">{p.receipt_no}</div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-navy-400 capitalize">{p.payment_method}</span>
                      <span className="font-medium text-navy-800">Rs. {p.amount.toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}